import React, { ChangeEvent, KeyboardEvent, ReactNode, RefObject, useEffect, useRef, useState } from 'react'

export interface IProps {
  dropdownClass?: string
  children: ReactNode
  currentValue: string
  placeholder?: string
  unit?: string
  rf?: string
  isValid?: boolean
  ariaLabel?: string
  searchEnabled?: boolean
  searchValue?: string
  iconClass?: string
  onSwitchVisibility?: (itemsVisible: boolean) => void
  onSearch?: (value: string) => void
}

import './style.css'

/**
 * Dropdown
 *
 * @param dropdownClass (string, optional) - Dropdown main class
 * @param children (ReactNode, required) - Dropdown option elements
 * @param currentValue (string, required) - Current chosen value
 * @param placeholder (string, optional) - Dropdown placeholder
 * @param rf (string, optional) - Optional tracking id for automated tests
 * @param isValid (boolean, optional) - Indicates value validation state
 * @param ariaLabel (string, optional) - Optional arial label for dropdown container
 * @param searchEnabled (boolean, optional) - Enabled search input when items are visible
 * @param searchValue (boolean, optional) - Given input value (searchEnabled === true)
 * @param onSearch (string, optional) - On search input callback (searchEnabled === true)
 * @param onSearch (string, optional) - On search input callback (searchEnabled === true)
 * @param onSwitchVisibility (void, optional) - On change items visibility state callback
 *
 */
export default function Dropdown(props: IProps) {
  const HIDDEN_CLASS = props.dropdownClass ? props.dropdownClass : 'csas-selectbox'
  const VISIBLE_CLASS = props.dropdownClass ? props.dropdownClass + ' open' : 'csas-selectbox open'

  const dropdownRef: RefObject<HTMLDivElement> = useRef()

  const [itemsVisible, setVisibility] = useState(false)
  const [isHiddenClass, setShowItemsClass] = useState(HIDDEN_CLASS)

  useEffect(() => {
    document.addEventListener('mousedown', handleOutsideClick)
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick)
    }
  }, [])

  const handleOutsideClick = (e: any) => {
    if (dropdownRef.current.contains(e.target)) {
      return
    }

    setVisibility(false)
    setShowItemsClass(HIDDEN_CLASS)
    if (props.onSwitchVisibility) {
      props.onSwitchVisibility(false)
    }
  }

  const setItemsVisibility = () => {
    const newState = !itemsVisible
    setVisibility(newState)
    if (newState) {
      setShowItemsClass(VISIBLE_CLASS)
    } else {
      setShowItemsClass(HIDDEN_CLASS)
    }
    if (props.onSwitchVisibility) {
      props.onSwitchVisibility(newState)
    }
  }

  const handleKeyPress = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' && itemsVisible) {
      setItemsVisibility()
    }
  }

  const handleSearch = (e: ChangeEvent<HTMLInputElement>) => props.onSearch && props.onSearch(e.target.value)

  const getValue = () => {
    if (props.unit) {
      return (
        <>
          {props.iconClass && <span className={props.iconClass} />}
          <strong>{props.currentValue} </strong>
          {props.unit}
        </>
      )
    } else {
      return (
        <>
          {props.iconClass && <span className={props.iconClass} />}
          {props.currentValue}
        </>
      )
    }
  }

  const finalClass =
    !props.isValid && props.isValid !== undefined && props.isValid !== null
      ? isHiddenClass + ' ' + 'csas-error'
      : isHiddenClass

  return (
    <div
      className={finalClass}
      onKeyPress={handleKeyPress}
      ref={dropdownRef}
      data-testid={props.rf}
      style={props.searchEnabled ? { borderColor: '#90A0B2' } : {}}
    >
      {itemsVisible && props.searchEnabled ? (
        <input
          type='text'
          className='form-control'
          value={props.searchValue}
          onChange={handleSearch}
          style={{ border: '1px solid #fff' }}
        />
      ) : (
        <button
          type='button'
          className='csas-selected'
          aria-expanded={itemsVisible}
          aria-label={props.ariaLabel}
          tabIndex={0}
          onClick={setItemsVisibility}
          style={{ width: '100%' }}
        >
          <span className='dropdown__button-text'>
            {!props.currentValue && props.placeholder ? props.placeholder : getValue()}
          </span>
          <span className='caret'></span>
        </button>
      )}
      <div className='csas-option-wrapper' onClick={setItemsVisibility}>
        <ol className='csas-option-wrapper-inner' role='listbox'>
          {props.children}
        </ol>
      </div>
    </div>
  )
}
